import { DVHData } from "./dvhTypes";

// Import the text files directly as raw strings
import automatedPlanContent from "./mockData/automated-plan.txt?raw";
import manualPlanContent from "./mockData/manual-plan.txt?raw";

// DVH File Parser function (based on dvhviewer parser)
export function parseDVHFile(fileContent: string): DVHData {
  const lines = fileContent.split("\n").map((line) => line.trim());

  // Initialize the result structure
  const result: DVHData = {
    patientInfo: {
      name: "Unknown Patient",
      id: "Unknown ID",
      comment: "DVH Analysis Data",
      date: new Date().toLocaleDateString(),
      exportedBy: "DVH Viewer System",
    },
    planInfo: {
      type: "Cumulative Dose Volume Histogram",
      description: "Imported DVH data for analysis",
      plan: "Treatment Plan",
      course: 1,
      planStatus: "Imported",
      totalDose: 5000,
      percentDose: 100,
    },
    structures: [],
  };

  type DVHStructure = DVHData["structures"][number];
  let currentStructure: DVHStructure | null = null;
  let isParsingDVHData = false;
  let lineIndex = 0;

  while (lineIndex < lines.length) {
    const line = lines[lineIndex];

    // Parse patient information
    if (line.includes("Patient Name")) {
      const value = extractValue(line);
      if (value && value.trim()) result.patientInfo.name = value.trim();
    } else if (line.includes("Patient ID")) {
      const value = extractValue(line);
      if (value && value.trim()) result.patientInfo.id = value.trim();
    } else if (line.includes("Comment")) {
      const value = extractValue(line);
      if (value && value.trim()) result.patientInfo.comment = value.trim();
    } else if (line.includes("Date")) {
      const value = extractValue(line);
      if (value && value.trim()) result.patientInfo.date = value.trim();
    } else if (line.includes("Exported by")) {
      const value = extractValue(line);
      if (value && value.trim()) result.patientInfo.exportedBy = value.trim();
    }

    // Parse plan information
    else if (line.includes("Type")) {
      const value = extractValue(line);
      if (value && value.trim()) result.planInfo.type = value.trim();
    } else if (line.includes("Description")) {
      const value = extractValue(line);
      if (value && value.trim()) result.planInfo.description = value.trim();
    } else if (line.includes("Plan:") && !line.includes("Plan Status")) {
      const value = extractValue(line);
      if (value && value.trim()) result.planInfo.plan = value.trim();
    } else if (line.includes("Course:")) {
      const value = parseFloat(extractValue(line));
      if (!isNaN(value)) result.planInfo.course = value;
    } else if (line.includes("Plan Status:")) {
      const value = extractValue(line);
      if (value && value.trim()) result.planInfo.planStatus = value.trim();
    } else if (line.includes("Total dose [cGy]:")) {
      const value = parseFloat(extractValue(line));
      if (!isNaN(value)) result.planInfo.totalDose = value;
    } else if (line.includes("% for dose (%):")) {
      const value = parseFloat(extractValue(line));
      if (!isNaN(value)) result.planInfo.percentDose = value;
    }

    // Parse structure information
    else if (line.includes("Structure:")) {
      // Save previous structure if it exists
      if (currentStructure) {
        result.structures.push(currentStructure);
      }

      // Start new structure
      const structureName = extractValue(line);
      currentStructure = {
        name: structureName || "Unknown Structure",
        approvalStatus: "Imported",
        plan: result.planInfo.plan,
        course: result.planInfo.course,
        volume: 0,
        doseCover: 100,
        samplingCover: 100,
        minDose: 0,
        maxDose: 0,
        meanDose: 0,
        modalDose: 0,
        medianDose: 0,
        std: 0,
        equivSphereDiam: 0,
        conformityIndex: "N/A",
        gradientMeasure: "N/A",
        doseLevel: "",
        rtogCI: "",
        paddickCI: "",
        gi: "",
        icrub3HI: "",
        dvhData: [],
      };
      isParsingDVHData = false;
    }

    // Parse structure details
    else if (currentStructure) {
      if (line.includes("Approval Status:")) {
        const value = extractValue(line);
        if (value && value.trim())
          currentStructure.approvalStatus = value.trim();
      } else if (line.includes("Volume [cm³]:")) {
        const value = parseFloat(extractValue(line));
        if (!isNaN(value)) currentStructure.volume = value;
      } else if (line.includes("Min Dose [%]:")) {
        const value = parseFloat(extractValue(line));
        if (!isNaN(value)) currentStructure.minDose = value;
      } else if (line.includes("Max Dose [%]:")) {
        const value = parseFloat(extractValue(line));
        if (!isNaN(value)) currentStructure.maxDose = value;
      } else if (line.includes("Mean Dose [%]:")) {
        const value = parseFloat(extractValue(line));
        if (!isNaN(value)) currentStructure.meanDose = value;
      } else if (line.includes("Modal Dose [%]:")) {
        const value = parseFloat(extractValue(line));
        if (!isNaN(value)) currentStructure.modalDose = value;
      } else if (line.includes("Median Dose [%]:")) {
        const value = parseFloat(extractValue(line));
        if (!isNaN(value)) currentStructure.medianDose = value;
      } else if (line.includes("STD [%]:")) {
        const value = parseFloat(extractValue(line));
        if (!isNaN(value)) currentStructure.std = value;
      } else if (line.includes("Equiv. Sphere Diam. [cm]:")) {
        const value = parseFloat(extractValue(line));
        if (!isNaN(value)) currentStructure.equivSphereDiam = value;
      }

      // Check for DVH data header
      else if (
        line.includes("Relative dose [%]") &&
        line.includes("Dose [cGy]")
      ) {
        isParsingDVHData = true;
        lineIndex++; // Skip the header line
        continue;
      }

      // Parse DVH data rows
      else if (isParsingDVHData && line && !line.includes(":")) {
        const parts = line.split(/\s+/).filter((part) => part.length > 0);
        if (parts.length >= 3) {
          const relativeDose = parseFloat(parts[0]);
          const dose = parseFloat(parts[1]);
          const ratio = parseFloat(parts[2]);

          if (!isNaN(relativeDose) && !isNaN(dose) && !isNaN(ratio)) {
            currentStructure.dvhData.push({
              relativeDose,
              dose,
              ratioTotalStructureVolume: ratio,
            });
          }
        }
      }
    }

    lineIndex++;
  }

  // Add the last structure if it exists
  if (currentStructure) {
    result.structures.push(currentStructure);
  }

  return result;
}

function extractValue(line: string): string {
  const colonIndex = line.indexOf(":");
  if (colonIndex !== -1) {
    return line.substring(colonIndex + 1).trim();
  }
  return "";
}

// Helper function to extract dose value from structure name
const extractDoseValue = (structureName: string): number | null => {
  // Look for dose patterns like 7000, 5000, etc.
  const doseMatch = structureName.match(/(\d{4,5})/);
  return doseMatch ? parseInt(doseMatch[1], 10) : null;
};

// Structure color mapping for consistent medical visualization
export const getStructureColor = (structureName: string): string => {
  // First check for specific High/Low variants
  const nameLower = structureName.toLowerCase();

  // CTV variants - check for dose-based naming first, then literal high/low
  if (nameLower.includes("ctv")) {
    const doseValue = extractDoseValue(structureName);
    if (doseValue !== null) {
      // Use 7000 cGy as the threshold for high vs low dose
      if (doseValue >= 7000) {
        return "#FF0000"; // Red for CTV High dose
      } else {
        return "#FFD700"; // Gold yellow for CTV Low dose
      }
    }
    // Fallback to literal high/low matching
    if (nameLower.includes("high")) {
      return "#FF0000"; // Red for CTV High
    }
    if (nameLower.includes("low")) {
      return "#FFD700"; // Gold yellow for CTV Low
    }
  }

  // PTV variants - check for dose-based naming first, then literal high/low
  if (nameLower.includes("ptv")) {
    const doseValue = extractDoseValue(structureName);
    if (doseValue !== null) {
      // Use 7000 cGy as the threshold for high vs low dose
      if (doseValue >= 7000) {
        return "#0000FF"; // Blue for PTV High dose
      } else {
        return "#32CD32"; // Kermit green for PTV Low dose
      }
    }
    // Fallback to literal high/low matching
    if (nameLower.includes("high")) {
      return "#0000FF"; // Blue for PTV High
    }
    if (nameLower.includes("low")) {
      return "#32CD32"; // Kermit green for PTV Low
    }
  }

  // Bowel variants (all purple)
  if (nameLower.includes("bowel")) {
    return "#8B008B"; // Purple for Bowel
  }

  const colorMap: { [key: string]: string } = {
    Bladder: "#FFEF4F", // Light yellow
    Rectum: "#8B4513", // Brown
    Prostate: "#EF4444", // Bright Red
    Femur_Head_L: "#10B981", // Emerald Green
    Femur_Head_R: "#06B6D4", // Cyan
    PTV: "#0000FF", // Blue (default PTV)
    PTV_Breast: "#0000FF", // Blue
    CTV: "#FF0000", // Red (default CTV)
    GTV: "#DC2626", // Strong Red
    Body: "#3B82F6", // Blue
    Skin: "#D97706", // Amber
    Penile_Bulb: "#7C3AED", // Violet
    Urethra: "#059669", // Teal
    Seminal_Vesicles: "#EA580C", // Deep Orange
    Neurovascular_Bundle_L: "#16A34A", // Green
    Neurovascular_Bundle_R: "#0D9488", // Teal
    CTVn: "#FF0000", // Red
    Colon_Sigmoid: "#2563EB", // Strong Blue
    Heart: "#DC2626", // Red
    Breast_L: "#F97316", // Orange
    Breast_R: "#FB923C", // Light Orange
    Lung_L: "#10B981", // Green
    Lung_R: "#06B6D4", // Cyan
  };

  // If structure has a predefined color, use it
  if (colorMap[structureName]) {
    return colorMap[structureName];
  }

  // Generate a vibrant, high-contrast color based on structure name hash
  let hash = 0;
  for (let i = 0; i < structureName.length; i++) {
    hash = structureName.charCodeAt(i) + ((hash << 5) - hash);
  }

  // Use predefined vibrant colors for better medical visualization
  const vibrantColors = [
    "#EF4444",
    "#F97316",
    "#F59E0B",
    "#84CC16",
    "#22C55E",
    "#10B981",
    "#06B6D4",
    "#0EA5E9",
    "#3B82F6",
    "#6366F1",
    "#8B5CF6",
    "#A855F7",
    "#C026D3",
    "#EC4899",
    "#F43F5E",
    "#DC2626",
    "#EA580C",
    "#D97706",
    "#65A30D",
    "#16A34A",
    "#059669",
    "#0891B2",
    "#0284C7",
    "#2563EB",
    "#4F46E5",
    "#7C3AED",
    "#9333EA",
    "#C333EA",
    "#DB2777",
    "#E11D48",
  ];

  const colorIndex = Math.abs(hash) % vibrantColors.length;
  return vibrantColors[colorIndex];
};

// Load and parse DVH data from files
export const loadDVHDataFromFiles = (): {
  planA: DVHData;
  planB: DVHData;
} => {
  // Load Plan A (Automated)
  const planA = parseDVHFile(automatedPlanContent);
  planA.planInfo.plan = "Plan A (Automated)";
  planA.planInfo.description = "Automated treatment plan";

  // Load Plan B (Manual)
  const planB = parseDVHFile(manualPlanContent);
  planB.planInfo.plan = "Plan B (Manual)";
  planB.planInfo.description = "Manual treatment plan";

  return { planA, planB };
};
